import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useAuth } from "react-oidc-context"
import { AppShell, currentIsoWeek } from "../components/AppShell"
import { useApi } from "../api/hooks"
import { listReceipts, type ReceiptRow } from "../api/receipts"
import { formatEuro } from "../lib/format"
import { useT } from "../lib/i18n"

export function HomePage() {
  const auth = useAuth()
  const api = useApi()
  const { t } = useT()
  const [latest, setLatest] = useState<ReceiptRow[] | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    listReceipts(api)
      .then((r) => setLatest(r.items.slice(0, 3)))
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
  }, [api])

  const week = currentIsoWeek()
  return (
    <AppShell>
      <h1 style={{ margin: 0, fontSize: 36 }}>
        {t("hi")}{auth.user?.profile?.given_name ? `, ${auth.user.profile.given_name}` : ""}.
      </h1>
      <p style={{ color: "var(--muted)", maxWidth: 640 }}>
        {t("home_intro")}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 32 }}>
        <Card title={t("card_new_receipt")} cta={t("card_upload_photo")} to="/receipts" />
        <Card title={t("card_current_week_diary")} cta={week} to={`/diary/${week}`} />
      </div>

      <h2 style={{ marginTop: 40, fontSize: 18, color: "var(--muted)" }}>{t("latest_receipts")}</h2>
      {err && <p style={{ color: "#ff7a85" }}>{t("error_prefix")}: {err}</p>}
      {!err && latest === null && <p style={{ color: "var(--muted)" }}>{t("loading")}</p>}
      {!err && latest && latest.length === 0 && <p style={{ color: "var(--muted)" }}>{t("nothing_yet")}</p>}
      {latest && latest.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, marginTop: 8 }}>
          {latest.map((r) => (
            <li key={r.SK} style={{ padding: "12px 0", borderTop: "1px solid var(--border)", display: "flex", gap: 16 }}>
              <span style={{ width: 100, color: "var(--muted)" }}>{r.purchase_date}</span>
              <span style={{ flex: 1 }}>{r.merchant ?? "—"}</span>
              <span style={{ width: 100, textAlign: "right" }}>
                {r.total_eur != null ? formatEuro(r.total_eur) : "—"}
              </span>
              <span style={{ color: r.status === "processed" ? "var(--accent)" : "var(--muted)", fontSize: 12 }}>
                {r.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  )
}

function Card({ title, cta, to }: { title: string; cta: string; to: string }) {
  return (
    <Link to={to} style={{
      display: "block", padding: 24, borderRadius: "var(--radius)",
      background: "var(--surface)", border: "1px solid var(--border)",
    }}>
      <p style={{ margin: 0, color: "var(--muted)", fontSize: 13 }}>{title}</p>
      <p style={{ margin: "6px 0 0 0", fontSize: 20, fontWeight: 600 }}>{cta} →</p>
    </Link>
  )
}
